import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { DateTime } from 'luxon'

import { CanUser, check } from './CanUser'

import FormInvitationDelete from './FormInvitationDelete'
import PageHeader from './PageHeader.js'
import PageSection from './PageSection.js'
import ReactDOMServer from 'react-dom/server'
import QRCode from 'qrcode.react'

import { Button } from './CommonStylesForms'
import { QRHolder } from './CommonStylesForms'
import {
  AttributeTable,
  AttributeRow,
  IconEdit,
  IconCheck,
  IconClose,
} from './CommonStylesTables'
import { LoadingHolder, Spinner } from './LoadingStyles'

const QR = styled(QRCode)`
  display: block;
  margin: auto;
  padding: 10px;
  width: 300px;
`

const EditInput = styled.input`
  width: 100%;
  padding: 5px;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 3px;
`

const EditSelect = styled.select`
  width: 100%;
  padding: 5px;
  margin-right: 10px;
  border: 1px solid #ccc;
  border-radius: 3px;
`

const EditButtonGroup = styled.div`
  display: inline-flex;
  gap: 5px;
  margin-left: 10px;
`

const SmallButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.save {
    ${IconCheck} {
      fill: #4caf50;
    }
  }

  &.cancel {
    ${IconClose} {
      fill: #f44336;
    }
  }

  ${IconEdit} {
    fill: ${(props) => props.theme.primary_color};
  }
`

function Invitation(props) {
  const invitationsState = useSelector((state) => state.invitations)
  const websocketsState = useSelector((state) => state.websockets)
  const invitationSelected = invitationsState.invitationSelected
  const { websocket, readyForWebsocketMessages } = websocketsState

  const [waitingForInvitation, setWaitingForInvitation] = useState(true)
  const [
    deleteInvitationModalIsOpen,
    setDeleteInvitationModalIsOpen,
  ] = useState(false)

  // Generic editing state
  const [editingField, setEditingField] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  const closeDeleteModal = () => setDeleteInvitationModalIsOpen(false)
  const deleteInvitation = () => {
    setDeleteInvitationModalIsOpen(true)
  }

  const { sendRequest, invitationId } = props

  // Define editable fields with their types and options
  const editableFields = {
    purpose: { type: 'text', label: 'Purpose' },
    description: { type: 'text', label: 'Description' },
    workflow_status: {
      type: 'select',
      label: 'Workflow Status',
      options: ['active', 'inactive', 'deleted'],
    },
    active_starting_at: {
      type: 'datetime-local',
      label: 'Active Starting Time',
    },
    active_ending_at: { type: 'datetime-local', label: 'Active Ending Time' },
    uses_allowed: { type: 'number', label: 'Uses Allowed' },
  }

  useEffect(() => {
    //(AmmonBurgi) Stop waiting for invitationSelected if the invitation_id matches the target ID. If no match is found, fetch the needed invitation.
    if (websocket && readyForWebsocketMessages) {
      if (
        Object.keys(invitationSelected).length !== 0 &&
        invitationSelected.invitation_id === parseInt(invitationId)
      ) {
        const endTimestamp = invitationSelected.active_ending_at
          ? DateTime.fromISO(invitationSelected.active_ending_at, {
              zone: 'utc',
            }).ts
          : undefined
        const timestamp = DateTime.now().toMillis()

        if (
          invitationSelected.workflow_status === 'active' &&
          timestamp > endTimestamp
        ) {
          //(AmmonBurgi) When the starting time and ending time indicates that the invitation should be inactive, update it to be inactive.

          sendRequest('INVITATIONS', 'UPDATE_STATUS', {
            invitation_id: invitationId,
            workflow_status: 'inactive',
          })
        } else {
          setWaitingForInvitation(false)
        }
      } else {
        setWaitingForInvitation(true)
        if (check('invitations:read')) {
          sendRequest('INVITATIONS', 'GET', {
            invitation_id: invitationId,
          })
        }
      }
    }
  }, [invitationSelected, invitationId, websocket, readyForWebsocketMessages])

  const startEditing = (fieldName) => {
    const currentValue = invitationSelected[fieldName] || ''

    // Format datetime values for datetime-local input
    if (editableFields[fieldName].type === 'datetime-local' && currentValue) {
      const formatted = new Date(currentValue).toISOString().slice(0, 16)
      setEditValue(formatted)
    } else {
      setEditValue(currentValue)
    }

    setEditingField(fieldName)
  }

  const cancelEditing = () => {
    setEditingField(null)
    setEditValue('')
  }

  const saveField = async () => {
    if (!editingField) return

    const currentValue = invitationSelected[editingField]
    let valueToSave = editValue

    // Handle datetime conversion
    if (editableFields[editingField].type === 'datetime-local' && valueToSave) {
      valueToSave = new Date(valueToSave).toISOString()
    }

    // Handle empty strings vs null/undefined for non-string fields
    if (
      valueToSave === '' &&
      editingField !== 'purpose' &&
      editingField !== 'description'
    ) {
      valueToSave = undefined
    }

    // Check if value actually changed
    if (valueToSave === currentValue) {
      cancelEditing()
      return
    }

    setIsUpdating(true)
    try {
      const updateData = {
        invitation_id: invitationId,
        [editingField]: valueToSave,
      }

      await sendRequest('INVITATIONS', 'UPDATE', updateData)
      setEditingField(null)
      setEditValue('')
    } catch (error) {
      console.error(`Failed to update invitation ${editingField}:`, error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveField()
    } else if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  const formatDisplayValue = (fieldName, value) => {
    if (!value) return ''

    if (editableFields[fieldName].type === 'datetime-local') {
      return new Date(value).toLocaleString()
    }

    if (fieldName === 'uses_allowed' && !value) {
      return <span>&infin;</span>
    }

    return value
  }

  const renderEditableField = (fieldName, displayValue) => {
    const field = editableFields[fieldName]
    const isEditing = editingField === fieldName

    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {field.type === 'select' ? (
            <EditSelect
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              disabled={isUpdating}
              autoFocus
            >
              <option value="">Select...</option>
              {field.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </EditSelect>
          ) : (
            <EditInput
              type={field.type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isUpdating}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              autoFocus
              min={field.type === 'number' ? '1' : undefined}
              max={field.type === 'number' ? '1000000' : undefined}
            />
          )}
          <EditButtonGroup>
            <SmallButton
              className="save"
              onClick={saveField}
              disabled={isUpdating}
              title="Save (Enter)"
            >
              {isUpdating ? '...' : <IconCheck />}
            </SmallButton>
            <SmallButton
              className="cancel"
              onClick={cancelEditing}
              disabled={isUpdating}
              title="Cancel (Escape)"
            >
              <IconClose />
            </SmallButton>
          </EditButtonGroup>
        </div>
      )
    }

    return (
      <div>
        <span>{displayValue}</span>
        <CanUser
          perform="invitations:update"
          yes={() => (
            <EditButtonGroup>
              <SmallButton
                onClick={() => startEditing(fieldName)}
                title={`Edit ${field.label.toLowerCase()}`}
              >
                <IconEdit />
              </SmallButton>
            </EditButtonGroup>
          )}
        />
      </div>
    )
  }

  return (
    <>
      {!waitingForInvitation ? (
        <div id="contact">
          <PageHeader
            title={'Invitation Details: ' + (invitationSelected.label || '')}
          />
          <PageSection>
            <h2>General Information</h2>
            <QRHolder style={{ float: 'right', textAlign: 'center' }}>
              {invitationSelected.invitation_url ? (
                invitationSelected.workflow_status === 'active' ? (
                  <>
                    <QR
                      value={invitationSelected.invitation_url}
                      size={256}
                      renderAs="svg"
                    />
                    <a
                      href={
                        'data:image/svg+xml;charset=utf-8,' +
                        encodeURIComponent(
                          ReactDOMServer.renderToString(
                            <QR
                              value={invitationSelected.invitation_url}
                              size={256}
                              renderAs="svg"
                              xmlns="http://www.w3.org/2000/svg"
                            />
                          )
                        )
                      }
                      download="qrcode.svg"
                      href-lang="image/svg+xml"
                      id="button-download-qr"
                    >
                      Download
                    </a>
                  </>
                ) : null
              ) : (
                <p>Loading...</p>
              )}
            </QRHolder>
            <AttributeTable>
              <tbody>
                <AttributeRow>
                  <th>Invitation ID:</th>
                  <td>{invitationSelected.invitation_id || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Contact ID:</th>
                  <td>{invitationSelected.contact_id || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Connection ID:</th>
                  <td>{invitationSelected.connection_id || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Invitation Message ID:</th>
                  <td>{invitationSelected.invitation_msg_id || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Invitation Key:</th>
                  <td>{invitationSelected.invitation_key || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Invitation Mode:</th>
                  <td>{invitationSelected.invitation_mode || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Accept:</th>
                  <td>{invitationSelected.accept || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Their Role:</th>
                  <td>{invitationSelected.their_role || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Their Label:</th>
                  <td>{invitationSelected.their_label || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Service Endpoint:</th>
                  <td>{invitationSelected.service_endpoint || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Domain:</th>
                  <td>{invitationSelected.domain || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Path:</th>
                  <td>{invitationSelected.path || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Workflow Status:</th>
                  <td>
                    {renderEditableField(
                      'workflow_status',
                      invitationSelected.workflow_status || ''
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Invitation State:</th>
                  <td>{invitationSelected.state || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Description:</th>
                  <td>
                    {renderEditableField(
                      'description',
                      invitationSelected.description || ''
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Purpose:</th>
                  <td>
                    {renderEditableField(
                      'purpose',
                      invitationSelected.purpose || ''
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Active Starting Time:</th>
                  <td>
                    {renderEditableField(
                      'active_starting_at',
                      formatDisplayValue(
                        'active_starting_at',
                        invitationSelected.active_starting_at
                      )
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Active Ending Time:</th>
                  <td>
                    {renderEditableField(
                      'active_ending_at',
                      formatDisplayValue(
                        'active_ending_at',
                        invitationSelected.active_ending_at
                      )
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Uses Allowed:</th>
                  <td>
                    {renderEditableField(
                      'uses_allowed',
                      formatDisplayValue(
                        'uses_allowed',
                        invitationSelected.uses_allowed
                      )
                    )}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Uses Total:</th>
                  <td>
                    {invitationSelected.uses_total ||
                    invitationSelected.uses_total === 0
                      ? invitationSelected.uses_total
                      : ''}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Created At:</th>
                  <td>
                    {invitationSelected.created_at
                      ? new Date(invitationSelected.created_at).toLocaleString()
                      : ''}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>Updated At:</th>
                  <td>
                    {invitationSelected.updated_at
                      ? new Date(invitationSelected.updated_at).toLocaleString()
                      : ''}
                  </td>
                </AttributeRow>
                <AttributeRow>
                  <th>My DID:</th>
                  <td>{invitationSelected.my_did || ''}</td>
                </AttributeRow>
                <AttributeRow>
                  <th>Invitation URL:</th>
                  <td
                    style={{
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                      maxWidth: '400px',
                      textDecoration:
                        invitationSelected.workflow_status !== 'active'
                          ? 'line-through'
                          : 'initial',
                    }}
                  >
                    {invitationSelected.invitation_url || ''}
                  </td>
                </AttributeRow>
              </tbody>
            </AttributeTable>
            <CanUser
              perform="invitations:delete"
              yes={() => (
                <Button
                  title="Delete Invitation"
                  onClick={() => {
                    deleteInvitation(invitationSelected.invitation_id)
                  }}
                  id="button-remove-invitation"
                >
                  Remove
                </Button>
              )}
            />
          </PageSection>

          <FormInvitationDelete
            sendRequest={sendRequest}
            history={props.history}
            invitationId={invitationId}
            deleteInvitationModalIsOpen={deleteInvitationModalIsOpen}
            closeDeleteModal={closeDeleteModal}
          />
        </div>
      ) : (
        <LoadingHolder>
          <p>Fetching invitation, please wait...</p>
          <Spinner />
        </LoadingHolder>
      )}
    </>
  )
}

export default Invitation
